import { ComponentType } from 'react';

import { Icons } from '@cast/design-system';
import { buildEnterpriseUrl } from '@cast/infrastructure-organization';
import type { SidebarNavRules } from './canViewNavItem';

import {
  BASE_PATHS,
  buildAccessControlUrl,
  buildCloudVmsPath,
  buildOverviewUrl,
  buildSettingsUrl,
  ORG_COST_REPORT_BASE,
  OVERVIEW_ROUTE_SEGMENTS,
} from '@oneclick/constants/navigation';
import { ACCESS_CONTROL_ROUTE_CONFIG } from '@oneclick/pages/access-control/accessControlRouteConfig';
import {
  CLUSTER_COST_MONITORING_SIDEBAR_IDS,
  CLUSTER_COST_REPORT_NAV,
  CLUSTER_INVENTORY_NAV,
  CLUSTER_ROUTE_CONFIG,
  CLUSTER_STORAGE_AUTOSCALER_NAV,
  CLUSTER_WOOP_NAV,
  flattenClusterNavItems,
} from '@oneclick/pages/cluster/clusterRouteConfig';
import {
  ORG_APA_CHILDREN,
  ORG_APA_SECTION,
  ORG_COST_MANAGEMENT_CHILDREN,
  ORG_COST_MONITORING_CHILDREN,
  ORG_DBO_CHILDREN,
  ORG_DBO_SECTION,
  ORG_EXTRA_FEATURES,
  ORG_FLEET_ITEMS,
  ORG_SETTINGS_CHILDREN,
  ORG_SIDEBAR_SECTION_TITLES,
} from '@oneclick/pages/orgRouteConfig';
import { SETTINGS_ROUTE_CONFIG } from '@oneclick/pages/settings/settingsRouteConfig';
import { flattenNavItems } from '@oneclick/routing/utils';

export { ORG_APA_SECTION, ORG_DBO_SECTION, ORG_SIDEBAR_SECTION_TITLES };

export const CLUSTER_SIDEBAR_SECTION_TITLES = {
  cluster: 'Cluster',
  inventory: 'Inventory',
  automation: 'Automation',
} as const;

export type SidebarIconComponent = ComponentType<{
  size?: number;
  weight?: 'regular' | 'bold' | 'fill' | 'duotone' | 'thin' | 'light';
  color?: string;
}>;

export const orgSidebarIcons: Record<string, SidebarIconComponent> = {
  clusters: Icons.Hexagon,
  overview: Icons.House,
  cloudVms: Icons.Cloud,
  databases: Icons.Database,
  dboCaches: Icons.Cylinder,
  dboCloudDatabases: Icons.Database,
  dboPerformanceAdvisor: Icons.ChartLineUp,
  apa: Icons.CastApa,
  clusterSchedules: Icons.Lightning,
  clusterHibernation: Icons.Moon,
  scheduledRebalancing: Icons.CalendarBlank,
  costMonitoring: Icons.ChartBar,
  clustersReport: Icons.Hexagon,
  allocationGroupsReport: Icons.AlignBottom,
  idleResourcesReport: Icons.Circle,
  nodeTemplatesReport: Icons.HardDrives,
  savingsReport: Icons.ChartLineUp,
  costManagement: Icons.CurrencyDollar,
  commitments: Icons.Handshake,
  priceAdjustments: Icons.Tag,
  logs: Icons.FileText,
  networkIntelligence: Icons.ChartLine,
  budgets: Icons.Wallet,
  customDashboards: Icons.SquaresFour,
  vulnerabilities: Icons.CastSecurity,
  orgSettings: Icons.GearSix,
  platformUsage: Icons.CreditCard,
  featureControl: Icons.CastCopilot,
  cloudIntegrations: Icons.Cloud,
  componentControl: Icons.Sliders,
  permissionsCenter: Icons.Key,
  organizations: Icons.CirclesFour,
  users: Icons.Users,
  userGroups: Icons.UsersThree,
  ssoConnections: Icons.Key,
  serviceAccounts: Icons.Robot,
  roleBindings: Icons.Sliders,
  resources: Icons.Cube,
};

export const clusterSidebarIcons: Record<string, SidebarIconComponent> = {
  back: Icons.ArrowLeft,
  dashboard: Icons.Gauge,
  workloadAutoscaler: Icons.CastWorkloadAutoscaler,
  nodeAutoscaler: Icons.CastNode,
  storageAutoscaler: Icons.HardDrive,
  overview: Icons.House,
  evictor: Icons.PaintBrushHousehold,
  costReport: Icons.ChartBar,
  clusterReport: Icons.Hexagon,
  inventoryNodes: Icons.HardDrives,
  workloadsReport: Icons.SquaresFour,
  namespacesReport: Icons.FolderSimple,
  allocationGroupsReport: Icons.AlignBottom,
  comparisonReport: Icons.ArrowsLeftRight,
  savingsReport: Icons.ChartLineUp,
  clusterScore: Icons.Star,
  optimizationConstraints: Icons.Sliders,
  availableSavings: Icons.ChartLineUp,
  vulnerabilities: Icons.CastSecurity,
  rebalancer: Icons.CastRebalancer,
  podMutations: Icons.Equalizer,
  logs: Icons.FileText,
};

type OrgFleetItemId = 'clusters' | 'overview' | 'cloudVms';

export const orgFleetItems: {
  id: OrgFleetItemId;
  title: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_FLEET_ITEMS.map((item) => ({
  id: item.id as OrgFleetItemId,
  title: item.title,
  rules: item.rules,
}));

export const resolveOrgFleetItemPath = (
  id: OrgFleetItemId
): string | undefined => {
  switch (id) {
    case 'clusters':
      return BASE_PATHS.clusters;
    case 'overview':
      return buildOverviewUrl(OVERVIEW_ROUTE_SEGMENTS.organization);
    case 'cloudVms':
      return buildCloudVmsPath();
    default:
      return undefined;
  }
};

export const isOrgFleetItemActive = (
  id: OrgFleetItemId,
  pathname: string
): boolean => {
  switch (id) {
    case 'clusters':
      return pathname === BASE_PATHS.clusters;
    case 'overview':
      return pathname.startsWith(BASE_PATHS.overview);
    case 'cloudVms':
      return pathname.startsWith(BASE_PATHS.cloudVms);
    default:
      return false;
  }
};

type OrgDboChildId = 'caches' | 'cloudDatabases' | 'performanceAdvisor';

export const orgDboChildren: {
  id: OrgDboChildId;
  title: string;
  segment: string;
  icon: keyof typeof orgSidebarIcons;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_DBO_CHILDREN.map((item) => ({
  id: item.id as OrgDboChildId,
  title: item.title,
  segment: item.segment!,
  icon: item.icon as keyof typeof orgSidebarIcons,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

type OrgApaChildId = 'runbooks' | 'integrations' | 'settings';

export const orgApaChildren: {
  id: OrgApaChildId;
  title: string;
  path: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
  requiresClustersWithApa?: boolean;
}[] = ORG_APA_CHILDREN.map((item) => ({
  id: item.id as OrgApaChildId,
  title: item.title,
  path: item.path,
  rules: item.rules,
  requiresClustersWithApa: item.requiresClustersWithApa,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

type OrgExtraFeaturesItemId = 'networkIntelligence';

export const orgExtraFeaturesItems: {
  id: OrgExtraFeaturesItemId;
  title: string;
  path: string;
  basePath: string;
  icon: keyof typeof orgSidebarIcons;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_EXTRA_FEATURES.map((item) => ({
  id: item.id as OrgExtraFeaturesItemId,
  title: item.title,
  path: item.path,
  basePath: item.basePath!,
  icon: item.icon as keyof typeof orgSidebarIcons,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

export const isOrgCostMonitoringActive = (pathname: string): boolean =>
  pathname.startsWith(ORG_COST_REPORT_BASE);

type OrgSchedulesChildId = 'clusterHibernation' | 'scheduledRebalancing';

export const orgSchedulesChildren: {
  id: OrgSchedulesChildId;
  title: string;
  path: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = flattenNavItems(SETTINGS_ROUTE_CONFIG)
  .filter(
    (item): item is typeof item & { id: OrgSchedulesChildId } =>
      item.id === 'clusterHibernation' || item.id === 'scheduledRebalancing'
  )
  .map((item) => ({
    id: item.id,
    title: item.title,
    path: buildSettingsUrl(item.route.replace(/\/\*$/, '')),
    rules: item.rules,
    hiddenWhenReportingLimited: true,
  }));

type OrgCostMonitoringChildId =
  | 'clustersReport'
  | 'allocationGroupsReport'
  | 'idleResourcesReport'
  | 'nodeTemplatesReport'
  | 'budgets'
  | 'customDashboards'
  | 'savingsReport';

export const orgCostMonitoringChildren: {
  id: OrgCostMonitoringChildId;
  title: string;
  path: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_COST_MONITORING_CHILDREN.map((item) => ({
  id: item.id as OrgCostMonitoringChildId,
  title: item.title,
  path: item.path,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

type OrgCostManagementChildId = 'commitments' | 'priceAdjustments';

export const orgCostManagementChildren: {
  id: OrgCostManagementChildId;
  title: string;
  path: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_COST_MANAGEMENT_CHILDREN.map((item) => ({
  id: item.id as OrgCostManagementChildId,
  title: item.title,
  path: item.path,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

type OrgSettingsChildId =
  | 'overview'
  | 'platformUsage'
  | 'featureControl'
  | 'cloudIntegrations'
  | 'componentControl'
  | 'permissionsCenter';

export const orgSettingsChildren: {
  id: OrgSettingsChildId;
  title: string;
  path: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = ORG_SETTINGS_CHILDREN.map((item) => ({
  id: item.id as OrgSettingsChildId,
  title: item.title,
  path: item.path,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

export const orgAccessControlChildren: {
  id: string;
  title: string;
  path: string;
  rules?: SidebarNavRules;
}[] = flattenNavItems(ACCESS_CONTROL_ROUTE_CONFIG)
  .filter((item) => !item.routeOnly && item.id)
  .map((item) => ({
    id: item.id!,
    title: item.title,
    path: buildAccessControlUrl(item.route),
    rules: item.rules,
  }));

/** SSO is a management sibling of access-control (matches console). */
export const orgSsoChild: {
  id: 'ssoConnections';
  title: string;
  path: string;
  rules: SidebarNavRules;
} = {
  id: 'ssoConnections',
  title: 'Single sign-on',
  path: BASE_PATHS.ssoConnections,
  rules: [['view', 'OrganizationSsoConnections']],
};

type EnterpriseSettingsChildId =
  | 'overview'
  | 'organizations'
  | 'users'
  | 'userGroups'
  | 'serviceAccounts'
  | 'ssoConnections'
  | 'platformUsage'
  | 'featureControl';

export const getEnterpriseSettingsChildren = (
  enterpriseId: string
): {
  id: EnterpriseSettingsChildId;
  title: string;
  path: string;
  icon: keyof typeof orgSidebarIcons;
  rules?: SidebarNavRules;
}[] => [
  {
    id: 'overview',
    title: 'Overview',
    path: buildEnterpriseUrl(enterpriseId, 'overview'),
    icon: 'overview',
  },
  {
    id: 'organizations',
    title: 'Organizations',
    path: buildEnterpriseUrl(enterpriseId, 'child-organizations'),
    icon: 'organizations',
  },
  {
    id: 'users',
    title: 'Users',
    path: buildEnterpriseUrl(enterpriseId, 'manage-users'),
    icon: 'users',
  },
  {
    id: 'userGroups',
    title: 'User groups',
    path: buildEnterpriseUrl(enterpriseId, 'manage-user-groups'),
    icon: 'userGroups',
  },
  {
    id: 'serviceAccounts',
    title: 'Service accounts',
    path: buildEnterpriseUrl(enterpriseId, 'manage-service-accounts'),
    icon: 'serviceAccounts',
  },
  {
    id: 'ssoConnections',
    title: 'Single sign-on',
    path: buildEnterpriseUrl(enterpriseId, 'sso-connections'),
    icon: 'ssoConnections',
  },
  {
    id: 'platformUsage',
    title: 'Platform usage',
    path: buildEnterpriseUrl(enterpriseId, 'enterprise-billing-report'),
    icon: 'costMonitoring',
  },
  {
    id: 'featureControl',
    title: 'Feature control',
    path: buildEnterpriseUrl(enterpriseId, 'feature-control'),
    icon: 'featureControl',
    rules: [['view', 'FeatureOrganizationManagementFeatureControl']],
  },
];

type ClusterCostMonitoringChildId =
  (typeof CLUSTER_COST_MONITORING_SIDEBAR_IDS)[number];

export const clusterCostMonitoringChildren: {
  id: ClusterCostMonitoringChildId;
  title: string;
  buildPath: (clusterPath: string, costReportBasePath: string) => string;
  isActive: (
    pathname: string,
    clusterPath: string,
    costReportBasePath: string
  ) => boolean;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = CLUSTER_COST_MONITORING_SIDEBAR_IDS.map((id) => {
  if (id === 'availableSavings') {
    const item = flattenClusterNavItems(CLUSTER_ROUTE_CONFIG).find(
      (leaf) => leaf.id === 'availableSavings'
    )!;
    return {
      id,
      title: item.title,
      buildPath: (clusterPath) => `${clusterPath}/available-savings`,
      isActive: (pathname, clusterPath) =>
        pathname.startsWith(`${clusterPath}/available-savings`),
      rules: item.rules,
      hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
    };
  }

  const item = CLUSTER_COST_REPORT_NAV.find((nav) => nav.id === id)!;
  return {
    id,
    title: item.title,
    buildPath: (clusterPath, costReportBasePath) => {
      const linkPath = item.defaultSubPath
        ? `${costReportBasePath}/${item.segment}/${item.defaultSubPath}`
        : `${costReportBasePath}/${item.segment}`;
      return linkPath;
    },
    isActive: (pathname, _clusterPath, costReportBasePath) =>
      pathname.startsWith(`${costReportBasePath}/${item.segment}`),
    rules: item.rules,
    hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
  };
});

type ClusterReportingItemId = 'clusterScore' | 'optimizationConstraints';

const CLUSTER_SCORE_ITEM = flattenClusterNavItems(CLUSTER_ROUTE_CONFIG).find(
  (leaf) => leaf.id === 'clusterScore'
)!;

const OPTIMIZATION_CONSTRAINTS_ITEM = flattenClusterNavItems(
  CLUSTER_ROUTE_CONFIG
).find((leaf) => leaf.id === 'optimizationConstraints')!;

export const clusterScoreNavItem: {
  id: ClusterReportingItemId;
  title: string;
  segment: string;
  isPrefix?: boolean;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  id: 'clusterScore',
  title: CLUSTER_SCORE_ITEM.title,
  segment: CLUSTER_SCORE_ITEM.segment!,
  isPrefix: CLUSTER_SCORE_ITEM.isPrefix,
  rules: CLUSTER_SCORE_ITEM.rules,
  hiddenWhenReportingLimited: CLUSTER_SCORE_ITEM.hiddenWhenReportingLimited,
};

export const optimizationConstraintsNavItem: {
  id: ClusterReportingItemId;
  title: string;
  segment: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  id: 'optimizationConstraints',
  title: OPTIMIZATION_CONSTRAINTS_ITEM.title,
  segment: OPTIMIZATION_CONSTRAINTS_ITEM.segment!,
  rules: OPTIMIZATION_CONSTRAINTS_ITEM.rules,
  hiddenWhenReportingLimited:
    OPTIMIZATION_CONSTRAINTS_ITEM.hiddenWhenReportingLimited,
};

type ClusterWoopChildId =
  | 'workloads'
  | 'events'
  | 'impact'
  | 'customMetrics'
  | 'hpa'
  | 'scalingPolicies';

export const clusterWoopChildren: {
  id: ClusterWoopChildId;
  title: string;
  segment: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = CLUSTER_WOOP_NAV.map((item) => ({
  id: item.id as ClusterWoopChildId,
  title: item.title,
  segment: item.segment!,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));

type ClusterInventoryChildId =
  | 'nodes'
  | 'namespaces'
  | 'allocationGroups'
  | 'workloads';

export const clusterInventoryChildren: {
  id: ClusterInventoryChildId;
  title: string;
  icon: keyof typeof clusterSidebarIcons;
  buildPath: (clusterPath: string, costReportBasePath: string) => string;
  isActive: (
    pathname: string,
    clusterPath: string,
    costReportBasePath: string
  ) => boolean;
  rules?: SidebarNavRules;
}[] = CLUSTER_INVENTORY_NAV.map((item) => {
  const segment = item.inventorySegment!;
  const defaultSub = item.defaultSubPath;
  return {
    id: item.id as ClusterInventoryChildId,
    title: item.title,
    icon: item.inventoryIcon as keyof typeof clusterSidebarIcons,
    buildPath: (clusterPath) =>
      defaultSub
        ? `${clusterPath}/inventory/${segment}/${defaultSub}`
        : `${clusterPath}/inventory/${segment}`,
    isActive: (pathname, clusterPath) =>
      pathname.startsWith(`${clusterPath}/inventory/${segment}`),
    rules: item.rules,
  };
});

export const clusterNodeAutoscalerNavItem: {
  title: string;
  buildPath: (autoscalerPath: string) => string;
  rules?: SidebarNavRules;
  kentRules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  title: 'Node autoscaler',
  buildPath: (autoscalerPath) => `${autoscalerPath}/settings`,
  rules: [['view', 'ClusterAutoscaler']],
  kentRules: [['view', 'ClusterKentKarpenterSettings']],
  hiddenWhenReportingLimited: true,
};

export const clusterBinPackingNavItem: {
  title: string;
  buildPath: (clusterPath: string) => string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  title: 'Bin-packing',
  buildPath: (clusterPath) => `${clusterPath}/bin-packing`,
  rules: [['view', 'ClusterAutoscaler']],
  hiddenWhenReportingLimited: true,
};

export const clusterRebalancerNavItem: {
  title: string;
  buildPath: (clusterPath: string) => string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  title: 'Rebalancer',
  buildPath: (clusterPath) => `${clusterPath}/rebalance`,
  rules: [['view', 'ClusterRebalancer']],
  hiddenWhenReportingLimited: true,
};

export const clusterPodMutationsNavItem: {
  title: string;
  buildPath: (settingsPath: string) => string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
} = {
  title: 'Pod mutations',
  buildPath: (settingsPath) => `${settingsPath}/pod-mutator`,
  rules: [['view', 'ClusterPodMutations']],
  hiddenWhenReportingLimited: true,
};

type ClusterStorageAutoscalerChildId =
  | 'persistentVolumes'
  | 'pvPolicies'
  | 'storageConfiguration'
  | 'events';

export const clusterStorageAutoscalerChildren: {
  id: ClusterStorageAutoscalerChildId;
  title: string;
  segment: string;
  rules?: SidebarNavRules;
  hiddenWhenReportingLimited?: boolean;
}[] = CLUSTER_STORAGE_AUTOSCALER_NAV.map((item) => ({
  id: item.id as ClusterStorageAutoscalerChildId,
  title: item.title,
  segment: item.segment!,
  rules: item.rules,
  hiddenWhenReportingLimited: item.hiddenWhenReportingLimited,
}));
